import { useNavigate } from "react-router-dom";

import { Button, Form, Input, message } from "antd";
import axios from "axios";
import { FC, useState } from "react";

import styles from "@/components/Account/ChangePassword/ChangePassword.module.css";
import OtpModal from "@/components/common/OTPModal";
import SuccessModal from "@/components/common/SuccessModal";
import { ROUTES_URL } from "@/constant";
import { changePassword, resendOtp, submitOtp } from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";
import { validatePassword } from "@/utils";

interface ChangePasswordForm {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const ChangePassword: FC = () => {
  const navigate = useNavigate();
  const logout = useAuthStore((store) => store?.logout);
  const [form] = Form.useForm<ChangePasswordForm>();
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const handleSubmit = async (values: ChangePasswordForm): Promise<void> => {
    try {
      await changePassword({
        new_password: values.new_password,
        old_password: values.old_password,
      });
      setOtpModal(true);
    } catch (error: unknown) {
      // if (axios.isAxiosError<{ message: string }>(error)) {
      //   message.error(error?.response?.data?.message || "Change password failed");
      // }
    }
  };

  const handleCloseModal = (): void => {
    setOtpModal(false);
  };

  const handleClosSuccesseModal = (): void => {
    setSuccessModal(false);
  };

  const handleSubmitOTP = async (otp: string): Promise<void> => {
    try {
      await submitOtp({
        otp,
      });
      setCountdown(5);
      handleCloseModal();
      setSuccessModal(true);

      let counter = 5;
      const timer = setInterval(() => {
        counter -= 1;
        setCountdown(counter);

        if (counter === 0) {
          clearInterval(timer);
          logout();
          navigate(ROUTES_URL.LOGIN, { replace: true });
        }
      }, 1000);
      form.resetFields();
    } catch (error: unknown) {
      // if (axios.isAxiosError<{ message: string }>(error)) {
      //   message.error(error?.response?.data?.message || "Submit OTP failed");
      // }
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    try {
      await resendOtp();
    } catch (error: unknown) {
      console.error(error);
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message);
      }
    }
  };

  return (
    <div className={styles.container}>
      <span className={styles.title}>Change Password</span>
      <div className={styles.formWrapper}>
        <div className={styles.onlyItems}>
          <Form
            form={form}
            id="passwordForm"
            layout="vertical"
            className={styles.formStyle}
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Old Password"
              name="old_password"
              required={false}
              rules={[{ required: true, message: "Old password is required" }]}
            >
              <Input.Password
                className={styles.customInput}
                placeholder="Enter old password"
                data-dd-privacy="mask"
              />
            </Form.Item>

            <Form.Item
              label="New Password"
              name="new_password"
              required={false}
              rules={[
                { required: true, message: "Please enter your new password" },
                {
                  validator: (_, value): Promise<void> => {
                    if (!value || validatePassword(value)) return Promise.resolve();
                    return Promise.reject(
                      new Error(
                        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
                      ),
                    );
                  },
                },
              ]}
            >
              <Input.Password
                className={styles.customInput}
                placeholder="Enter new password"
                data-dd-privacy="mask"
              />
            </Form.Item>

            <Form.Item
              label="Confirm new password"
              name="confirm_password"
              required={false}
              dependencies={["new_password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("new_password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords does not match"));
                  },
                }),
              ]}
            >
              <Input.Password
                className={styles.customInput}
                placeholder="Confirm new password"
                data-dd-privacy="mask"
              />
            </Form.Item>
          </Form>
        </div>
      </div>
      <div className={styles.applyButtonGroup}>
        <Button
          className={styles.applyButton}
          form="passwordForm"
          htmlType="submit"
          loading={loading}
        >
          Next
        </Button>
      </div>

      {otpModal && (
        <OtpModal
          open={otpModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmitOTP}
          onSubmitResend={handleResendOTP}
        />
      )}

      {successModal && (
        <SuccessModal
          open={successModal}
          onClose={handleClosSuccesseModal}
          title="Password has been changed successfully"
          subTitle={`You’ll be automatically signed out in ${countdown} second${countdown > 1 ? "s" : ""}.`}
        />
      )}
    </div>
  );
};

export default ChangePassword;
